import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';

import type { ServiceReport } from '@/features/service-locations/api/serviceLocations';

interface Measurement {
  name?: string;
  value?: string | number | null;
  unit_of_measurement?: string;
}

export function ServiceReportDetails({ report }: { report: ServiceReport }) {
  const readings = flattenMeasurements(report.readings);
  const dosages = flattenMeasurements(report.dosages);
  const checklist = flattenStrings(report.checklists);
  const completed = new Set(flattenStrings(report.checklist_items_complete));
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          label={report.completed_at ? 'Completed' : 'In progress'}
          color={report.completed_at ? 'success' : 'warning'}
        />
        <Typography color="text.secondary">
          Started {formatDate(report.created_at)}
          {report.user?.name ? ` · ${report.user.name}` : ''}
        </Typography>
      </Stack>
      <DataSection title="Readings" rows={readings} />
      <DataSection title="Dosages" rows={dosages} />
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="h2" sx={{ mb: 1 }}>
          Checklist
        </Typography>
        {checklist.length ? (
          checklist.map((item) => (
            <Stack
              key={item}
              direction="row"
              justifyContent="space-between"
              sx={{ py: 0.75, borderTop: '1px solid #edebe9' }}
            >
              <Typography>{item}</Typography>
              <Chip
                size="small"
                label={completed.has(item) ? 'Complete' : 'Outstanding'}
                color={completed.has(item) ? 'success' : 'default'}
              />
            </Stack>
          ))
        ) : (
          <Typography color="text.secondary">No checklist items recorded.</Typography>
        )}
      </Paper>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <Note title="General notes" value={report.notes} />
        <Note title="Notes for management" value={report.notes_for_management} />
        <Note title="Notes for client" value={report.notes_for_client} />
      </Stack>
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="h2" sx={{ mb: 1 }}>
          Photos
        </Typography>
        {report.photos.length ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))',
              gap: 1,
            }}
          >
            {report.photos.map((photo, index) => (
              <Box
                component="a"
                href={photo.src}
                target="_blank"
                rel="noreferrer"
                key={`${photo.src}-${index}`}
              >
                <Box
                  component="img"
                  src={photo.msrc ?? photo.src}
                  alt={photo.title ?? `Service report photo ${index + 1}`}
                  loading="lazy"
                  sx={{ width: '100%', height: 130, objectFit: 'cover', borderRadius: 1 }}
                />
                <Typography variant="caption">{photo.title ?? 'Service report photo'}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary">No photos attached.</Typography>
        )}
      </Paper>
      {!report.completed_at && (
        <Alert severity="info">This report is still in progress and may change.</Alert>
      )}
    </Stack>
  );
}

function DataSection({ title, rows }: { title: string; rows: Measurement[] }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {rows.length ? (
        rows.map((row, index) => (
          <Stack
            key={`${row.name}-${index}`}
            direction="row"
            justifyContent="space-between"
            sx={{ py: 0.75, borderTop: '1px solid #edebe9' }}
          >
            <Typography>{row.name ?? 'Unnamed'}</Typography>
            <Typography>
              {row.value ?? '—'} {row.unit_of_measurement ?? ''}
            </Typography>
          </Stack>
        ))
      ) : (
        <Typography color="text.secondary">No {title.toLowerCase()} recorded.</Typography>
      )}
    </Paper>
  );
}
function Note({ title, value }: { title: string; value?: string | null }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
      <Typography fontWeight={600}>{title}</Typography>
      <Typography sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{value || '—'}</Typography>
    </Paper>
  );
}
function parseRecord(value?: string | null): Record<string, unknown> {
  if (!value) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}
function flattenMeasurements(value?: string | null): Measurement[] {
  return Object.values(parseRecord(value)).flatMap((entry) =>
    Array.isArray(entry)
      ? entry.filter((item): item is Measurement => typeof item === 'object' && item !== null)
      : [],
  );
}
function flattenStrings(value?: string | null): string[] {
  return Object.values(parseRecord(value)).flatMap((entry) =>
    Array.isArray(entry) ? entry.filter((item): item is string => typeof item === 'string') : [],
  );
}
function formatDate(value: string) {
  return new Date(value).toLocaleString('en-GB');
}
